import React, { useEffect, useState } from "react";
import { BiSortAlt2 } from "react-icons/bi";
import { LuSearch } from "react-icons/lu";
import { getNewHeader, getSortedResults } from "../../../utils/helpers";
import useOutsideClick from "../../hooks/useOutsideClick";
import SortItem from "./sort-item";
import styles from "./sort-section.module.css";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * SortFieldSection Component
 * 
 * Manages datatable column sorting with support for multiple sort conditions.
 * Provides UI to add/remove sort rules with field selection and sort direction (ASC/DESC).
 * Supports text, numeric, date, and boolean field sorting with appropriate sort order options.
 * Transforms sort conditions into backend query format.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.dispatch - Dispatch for sort state updates
 * @param {boolean} props.isLoading - Loading state indicator
 * @param {Array} props.sortConditionsArray - Current sort conditions
 * @param {string} props.argument - Sort argument string for backend
 * @param {string} props.collection - Data collection being sorted
 * @param {Array} props.fields - Available fields for sorting
 * @returns {JSX.Element} Sort management interface with condition builders
 */
export const sortOrders_text = [
  { value: " Sort A to Z", display: "A → Z" },
  { value: " Sort Z to A", display: "Z → A" },
];

export const sortOrders_quantity = [
  { value: " Sort smaller to larger", display: "A → Z" },
  { value: " Sort larger to smaller", display: "Z → A" },
];

export const sortOrders_date = [
  { value: " Sort older to newer", display: "A → Z" },
  { value: " Sort newer to older", display: "Z → A" },
];

// these sorts are only applicable for the isActive field which is the only boolean field there
// is, (might have to) change this when there are more boolean type fields
export const sortOrders_boolean = [
  {
    // "unknown" -> "inactive" -> "active"
    value: " Sort Z to A",
    display: "A → Z",
  },
  // "active" -> "inactive" -> "unknown"
  { value: " Sort A to Z", display: "Z → A" },
];

const SortFieldSection = ({ props }) => {
  const { supabase } = useSupabaseApp();

  const {
    dispatch,
    isLoading,
    sortConditionsArray,
    argument,
    collection,
    fields,
  } = props;

  const { ref, isOpen, toggleNav } = useOutsideClick();

  // the sort condition object
  const sortConditionObj = {
    actualName: "",
    sortOrder: "",
    type: "",
  };

  const push = (obj) => {
    dispatch({
      stateArray: "sortConditionsArray",
      type: "push_array",
      value: obj,
    });
  };

  // this function is only applicable when the system can only sort one field at a time
  const handleUpdate = (arr) => {
    const argumentCopy = {
      ...argument,
      selectedHeader: getNewHeader("companies", arr.actualName),
      selectedDropdown: arr.sortOrder,
    };

    //argument is updated
    dispatch({
      type: "update_state_when_sorting",
      argument: argumentCopy,
      fieldObj: arr,
    });
  };

  const update = (id, array) => {
    dispatch({
      stateArray: "sortConditionsArray",
      type: "update_array",
      index: id,
      newElement: { ...array },
    });
  };

  const remove = (id) => {
    dispatch({
      stateArray: "sortConditionsArray",
      type: "remove_item_from_array",
      index: id,
    });
  };

  const clear = () => {
    dispatch({ stateArray: "sortConditionsArray", type: "clear_array" });
  };

  const [searchValue, setSearchValue] = useState("");

  const handleAddSort = (item) => {
    let sortOrder;

    if (item.type === "quantity") {
      sortOrder = sortOrders_quantity[0].value;
    }

    if (item.type === "text") {
      sortOrder = sortOrders_text[0].value;
    }

    if (item.type === "date") {
      sortOrder = sortOrders_date[0].value;
    }

    if (item.type === "boolean") {
      sortOrder = sortOrders_boolean[0].value;
    }

    // add condition object to the overall array
    handleUpdate({
      actualName: item.actualName,
      sortOrder: sortOrder,
      type: item.type,
    });
  };

  // const handleOnChange = (e) => {
  //   update(0, { ...array[0], actualName: e.target.value });
  // };

  const handleOnChange = (e) => {
    setSearchValue(e.target.value);
  };

  // every time sortConditionsArray changes, sort datatable
  useEffect(() => {
    // console.log("sort conditions array changing:", sortConditionsArray);
    getSortedResults({ supabase, ...props });
  }, [sortConditionsArray]);

  return (
    <div
      ref={ref}
      className={"shutdownfilterdiv dropdown"}
      sortapplied={sortConditionsArray.length === 0 ? null : "true"}
    >
      <span
        className={
          isLoading ? "disabled shutdownfilterbuttons" : "shutdownfilterbuttons"
        }
        // style={{ backgroundColor: argument["filter"]["applied"] && " #bbf7d0" }}
        data-tip="Sort fields"
        onClick={!isLoading ? () => toggleNav() : () => { }}
      >
        <span>
          <BiSortAlt2 size={"1.5rem"} />
        </span>
        <span className="desktop">
          Sort
          <span
            style={{
              display: sortConditionsArray.length === 0 ? "none" : "inline",
            }}
          >
            ed
          </span>
        </span>
      </span>

      <div style={{ display: isOpen ? "block" : "none" }}>
        <div
          className={styles.menu}
          onClick={(e) => {
            // do not close modal if anything inside modal content is clicked
            e.stopPropagation();
          }}
        >
          <>
            <div
              className={styles.menuheader}
              style={{ color: "rgb(102, 102, 102)" }}
            >
              Sort by
            </div>
            <div className={styles.hsection}></div>
            <div className={styles.menucontent}>
              {/* {sortedFieldNames?.map((item, index) => (
                <SortItem key={index} props={props} />
              ))} */}

              <div
                style={{
                  display: sortConditionsArray.length > 0 ? "none" : "block",
                }}
              >
                <div className={styles.itemselectiondiv}>
                  <LuSearch />
                  <input
                    type="search"
                    value={searchValue}
                    className={styles.fieldsearchinput}
                    placeholder="Search a field"
                    onChange={(e) => handleOnChange(e)}
                  ></input>
                </div>
                {fields.map(
                  (field, index) =>
                    field.displayName
                      .toLowerCase()
                      .includes(searchValue.toLowerCase()) && (
                      <div
                        key={index}
                        className={styles.itemselectiondiv}
                        onClick={() => handleAddSort(field)}
                      >
                        {field.displayName}
                      </div>
                    )
                )}
              </div>

              {sortConditionsArray.length > 0 &&
                sortConditionsArray.map((field, index) => (
                  <SortItem
                    key={index}
                    id={index}
                    collection={collection}
                    field={field}
                    fields={fields}
                    update={handleUpdate}
                    remove={remove}
                  />
                ))}

              {/* {array.map((item, index) => {
                  if (item.type?.includes("group")) {
                    return (
                      <ConditionGroupItem
                        key={index}
                        connective={connective}
                        setConnective={setConnective}
                        array={array}
                        id={index}
                        remove={remove}
                        update={update}
                      />
                    );
                  }

                  return (
                    <FilterItem
                      key={index}
                      fields={fields}
                      connective={connective}
                      setConnective={setConnective}
                      array={array}
                      id={index}
                      remove={remove}
                      update={update}
                    />
                  );
                })} */}
            </div>
          </>

          <div
            className={styles.menufooter}
            style={{ color: "rgb(102, 102, 102)" }}
          >
            {/* <div
              className="flex gap-3 pointer"
              style={{ whiteSpace: "nowrap" }}
              onClick={handleAddCondition}
            >
              <BiPlus />
              <span>Add sort</span>
            </div> */}

            {/* {array.length > 0 && (
              <div
                className="flex pointer"
                style={{ whiteSpace: "nowrap", gap: "20px" }}
              >
                <TbFilterX
                  data-tip="Clear all filters"
                  onClick={handleResetFilter}
                  className=""
                  size={"1.2rem"}
                />

                <LuSearch
                  data-tip="Search"
                  className=""
                  onClick={handleSubmitFilter}
                  size={"1.2rem"}
                />
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SortFieldSection;
